import { forwardRef, useMemo } from 'react'
import Cropper from 'react-cropper'
import PropTypes from 'prop-types'

import ColumnWidthOutlinedIcon from '@ant-design/icons/ColumnWidthOutlined'
import RotateLeftOutlinedIcon from '@ant-design/icons/RotateLeftOutlined'
import RotateRightOutlinedIcon from '@ant-design/icons/RotateRightOutlined'

import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import IconButton from '@components/_mui/IconButton'

import 'cropperjs/dist/cropper.css'
import './ImageCrop.css'

/**
 * Inline Image Cropper component.
 *
 * @example
 *  const cropperRef = useRef()
 *
 *  const getCroppedImage = () => cropperRef.current?.cropper.getCroppedCanvas().toDataURL('image/jpeg')
 *
 *  return (
 *   <Stack>
 *    <ImageCropStatic ref={cropperRef} file={file} cropType="square" />
 *    <Button onClick=(getCroppedImage) />
 *   </Stack>
 *  )
 */
const ImageCropStatic = forwardRef((props, cropperRef) => {
  const { hideRotation = false, hideFlip = false, file, cropType = undefined, sx = {}, cropperStyles } = props

  const showRotation = !hideRotation
  const showFlip = !hideFlip

  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const aspectRatio = useMemo(() => {
    if (cropType === 'card') return 16 / 9
    if (cropType === 'square') return 1
    return NaN // freeform cropping
  }, [cropType])

  const handleRotate = (degrees) => {
    // We don't need to worry about going over 360 as the cropper handles that and wraps back to 0
    cropperRef.current?.cropper.rotate(degrees)
  }

  const handleFlipHorizontal = () => {
    const rotation = cropperRef.current?.cropper.getData().rotate

    if (Math.abs(rotation) === 90 || Math.abs(rotation) === 270) {
      // We need to scale the opposite axis when rotated 90 or 270 degrees
      const currentScaleY = cropperRef.current?.cropper.getData().scaleY
      cropperRef.current?.cropper.scaleY(currentScaleY === 1 ? -1 : 1)
      return
    }

    const currentScaleX = cropperRef.current?.cropper.getData().scaleX
    cropperRef.current?.cropper.scaleX(currentScaleX === 1 ? -1 : 1)
  }

  const handleFlipVertical = () => {
    const rotation = Math.abs(cropperRef.current?.cropper.getData().rotate)

    if (Math.abs(rotation) === 90 || Math.abs(rotation) === 270) {
      // We need to scale the opposite axis when rotated 90 or 270 degrees
      const currentScaleX = cropperRef.current?.cropper.getData().scaleX
      cropperRef.current?.cropper.scaleX(currentScaleX === 1 ? -1 : 1)
      return
    }

    const currentScaleY = cropperRef.current?.cropper.getData().scaleY
    cropperRef.current?.cropper.scaleY(currentScaleY === 1 ? -1 : 1)
  }

  return (
    <Fade in>
      <Stack
        sx={[
          {
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Cropper
          aspectRatio={aspectRatio}
          autoCrop={true}
          autoCropArea={0.9}
          background={false}
          center={true}
          checkOrientation={true} // https://github.com/fengyuanchen/cropperjs/issues/671
          guides={false}
          initialAspectRatio={aspectRatio}
          minCropBoxHeight={10}
          minCropBoxWidth={10}
          viewMode={2}
          responsive={true}
          ref={cropperRef}
          src={file}
          zoomable={true}
          zoomTo={0}
          style={cropperStyles || { height: smallScreen ? '250px' : '400px', width: '100%' }}
        />
        {(showRotation || showFlip) && (
          <Box
            sx={{
              py: 1,
              px: 4,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {showRotation && (
              <>
                <IconButton onClick={() => handleRotate(-90)}>
                  <RotateLeftOutlinedIcon />
                </IconButton>
                <IconButton sx={{ marginRight: 2 }} onClick={() => handleRotate(90)}>
                  <RotateRightOutlinedIcon />
                </IconButton>
              </>
            )}
            {showFlip && (
              <>
                <IconButton onClick={handleFlipHorizontal}>
                  <ColumnWidthOutlinedIcon />
                </IconButton>
                <IconButton onClick={handleFlipVertical}>
                  <ColumnWidthOutlinedIcon rotate={90} />
                </IconButton>
              </>
            )}
          </Box>
        )}
      </Stack>
    </Fade>
  )
})

ImageCropStatic.propTypes = {
  /**
   * Can have a fixed crop shape, allowed values are 'square', 'card', or undefined.
   * Defaults to undefined - free form cropping.
   * */
  cropType: PropTypes.string,

  /** Hide rotation buttons */
  hideRotation: PropTypes.bool,

  /** Hide flip buttons */
  hideFlip: PropTypes.bool,

  /** File in base 64 (string) */
  file: PropTypes.string,

  /** Can pass mui sx style */
  sx: PropTypes.object,

  /** Can pass cropper styles */
  cropperStyles: PropTypes.object,
}

export default ImageCropStatic
